import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import {
  GraduationCap, ChevronRight, ChevronLeft, X, Check, 
  Home, HeadphonesIcon, Calendar, Upload, MessageSquare, 
  HelpCircle, Lightbulb, Bug, Move, Shield
} from 'lucide-react';

// Customer Portal Tutorial Steps
const TUTORIAL_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to Chinook Manufacturing Support Services!',
    content: 'This is your personal space to manage your account, get support, and interact with us. Let us show you around!',
    tip: 'You can drag this window anywhere on the screen.',
    position: 'center',
    icon: GraduationCap
  },
  {
    id: 'home',
    title: 'Your Dashboard',
    content: 'Your home page shows an overview of your uploads, testimonials, and quick actions. You can see your recent activity at a glance.',
    link: '/my-portal',
    icon: Home
  },
  {
    id: 'support',
    title: 'Get Support',
    content: 'Need help? Create support tickets and track their status. Our team will respond as quickly as possible.',
    tip: 'You can attach files to your support tickets for better context.',
    link: '/my-portal/support',
    icon: HeadphonesIcon
  },
  {
    id: 'warranties',
    title: 'Warranty Claims',
    content: 'View your product warranties and file claims if something goes wrong. Track the status of your claims here.',
    tip: 'Keep your purchase receipts handy for warranty claims.',
    link: '/my-portal/warranties',
    icon: Shield
  },
  {
    id: 'booking',
    title: 'Book a Meeting',
    content: 'Schedule meetings with our team directly from the portal. Choose a time that works for you.',
    link: '/my-portal/book-meeting',
    icon: Calendar
  },
  {
    id: 'uploads',
    title: 'My Uploads',
    content: 'Share files, photos, and documents with us. Perfect for sharing equipment photos, issues, or project files.',
    tip: 'Supported formats include images, PDFs, and documents.',
    link: '/my-portal/files',
    icon: Upload
  },
  {
    id: 'testimonials',
    title: 'Share Your Experience',
    content: 'Had a great experience? Share your testimonial with us! Approved testimonials may be featured on our website.',
    link: '/my-portal/testimonials',
    icon: MessageSquare
  },
  {
    id: 'bug-report',
    title: 'Report Issues',
    content: 'Found something not working right? Use the bug report icon in the header to let us know. We appreciate your feedback!',
    tip: 'Click the bug icon in the top right corner to report any issues you encounter.',
    icon: Bug
  },
  {
    id: 'complete',
    title: "You're All Set!",
    content: "That's everything you need to know to get started. If you have questions, don't hesitate to reach out through the Support section.",
    tip: 'Check "Don\'t show this again" if you\'re familiar with the portal.',
    position: 'center',
    icon: Check
  }
];

/**
 * CustomerPortalTutorial - Draggable tutorial system for customer portal
 * Shows a welcome guide for new customers that can be moved around
 */
export const CustomerPortalTutorial = () => {
  const navigate = useNavigate();
  const tooltipRef = useRef(null);
  const [showTutorial, setShowTutorial] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [position, setPosition] = useState({ x: null, y: null });

  useEffect(() => {
    // Check if tutorial should be shown
    const tutorialDisabled = localStorage.getItem('customer_portal_tutorial_disabled');
    if (!tutorialDisabled) {
      // Small delay to let the page load
      const timer = setTimeout(() => setShowTutorial(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  // Drag handlers
  const handleMouseDown = (e) => {
    if (e.target.closest('[data-drag-handle]')) {
      setIsDragging(true);
      const rect = tooltipRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
      e.preventDefault();
    }
  };

  const handleMouseMove = useCallback((e) => {
    if (isDragging && tooltipRef.current) {
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;
      
      // Keep within viewport bounds
      const rect = tooltipRef.current.getBoundingClientRect();
      const maxX = window.innerWidth - rect.width;
      const maxY = window.innerHeight - rect.height;
      
      setPosition({
        x: Math.min(Math.max(0, newX), maxX),
        y: Math.min(Math.max(0, newY), maxY)
      });
    }
  }, [isDragging, dragOffset]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const handleNext = () => {
    if (currentStep < TUTORIAL_STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleComplete = () => {
    if (dontShowAgain) {
      localStorage.setItem('customer_portal_tutorial_disabled', 'true');
    }
    setShowTutorial(false);
  };

  const handleSkip = () => {
    if (dontShowAgain) {
      localStorage.setItem('customer_portal_tutorial_disabled', 'true');
    }
    setShowTutorial(false);
  };

  const handleNavigate = (path) => {
    if (path) {
      navigate(path);
    }
  };

  const step = TUTORIAL_STEPS[currentStep];
  const StepIcon = step?.icon || HelpCircle;

  if (!showTutorial) return null;

  // Calculate position style
  const getPositionStyle = () => {
    if (position.x !== null && position.y !== null) {
      return {
        position: 'fixed',
        left: position.x,
        top: position.y,
        transform: 'none'
      };
    }
    // Default position - top right
    return {
      top: '6rem',
      right: '2rem',
      left: 'auto',
      transform: 'none'
    };
  };

  return (
    <div className="fixed inset-0 z-[100] pointer-events-none">
      {/* Semi-transparent overlay - clickable to dismiss */}
      <div 
        className="absolute inset-0 bg-black/10 pointer-events-auto"
        onClick={handleSkip}
      />
      
      {/* Draggable Tutorial Card */}
      <div 
        ref={tooltipRef}
        className={`absolute bg-white rounded-2xl shadow-2xl max-w-md pointer-events-auto overflow-hidden border-2 border-blue-200 ${isDragging ? 'cursor-grabbing' : ''}`}
        style={getPositionStyle()}
        onMouseDown={handleMouseDown}
      >
        {/* Header - DRAG HANDLE */}
        <div 
          data-drag-handle
          className="bg-gradient-to-r from-blue-600 to-indigo-600 px-5 py-4 text-white cursor-grab active:cursor-grabbing select-none"
        >
          <div className="flex items-center justify-between mb-2">
            <Badge className="bg-white/20 text-white border-0">
              Step {currentStep + 1} of {TUTORIAL_STEPS.length}
            </Badge>
            <div className="flex items-center gap-2">
              <span className="text-xs text-white/60 flex items-center gap-1">
                <Move className="h-3 w-3" /> Drag to move
              </span>
              <button
                onClick={handleSkip}
                className="p-1 hover:bg-white/20 rounded-full transition-colors"
                data-testid="customer-tutorial-close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center">
              <StepIcon className="h-5 w-5 text-white" />
            </div>
            <h3 className="text-lg font-bold">{step.title}</h3>
          </div>
        </div>

        {/* Content */}
        <div className="p-5">
          <p className="text-slate-600 leading-relaxed">{step.content}</p>
          
          {step.tip && (
            <div className="mt-3 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
              <Lightbulb className="h-4 w-4 text-amber-600 mt-0.5" />
              <p className="text-sm text-amber-700">{step.tip}</p>
            </div>
          )}

          {step.link && (
            <Button
              variant="outline"
              size="sm"
              className="mt-3"
              onClick={() => handleNavigate(step.link)}
              data-testid="customer-tutorial-go-to-section"
            >
              Go to this section <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 bg-slate-50 border-t">
          {currentStep === TUTORIAL_STEPS.length - 1 && (
            <div className="flex items-center gap-2 mb-3">
              <Checkbox
                id="dontShow"
                checked={dontShowAgain}
                onCheckedChange={setDontShowAgain}
              />
              <label htmlFor="dontShow" className="text-sm text-slate-600 cursor-pointer">
                Don't show this tutorial again
              </label>
            </div>
          )}
          
          <div className="flex items-center justify-between">
            <div>
              {currentStep > 0 && (
                <Button variant="ghost" onClick={handlePrev} data-testid="customer-tutorial-prev">
                  <ChevronLeft className="h-4 w-4 mr-1" /> Previous
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleSkip} data-testid="customer-tutorial-skip">
                Skip
              </Button>
              <Button onClick={handleNext} className="bg-blue-600 hover:bg-blue-700" data-testid="customer-tutorial-next">
                {currentStep === TUTORIAL_STEPS.length - 1 ? 'Finish' : 'Next'}
                {currentStep < TUTORIAL_STEPS.length - 1 && <ChevronRight className="h-4 w-4 ml-1" />}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerPortalTutorial;
